import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';

import { ArielleContextType } from './types/arielleContextType';
import { useSocket } from '../hooks/useSocket';

import useCurrentUser from '../hooks/useCurrentUser';

import { ArielleResponse, ProjectDetailsResponse, ProjectRoadmapResponse, TaskBriefDescriptionResponse, TaskDefinitionOfDoneResponse, TaskDescriptionResponse, TaskTitleResponse } from '../data_models/aiModels/arielleResponseModels';
import { EArielleMessageType, EArielleProcessingType } from '../data_models/aiModels/enums';
import { EUserType } from '../data_models/enums';
import { IEmployer } from '../data_models/employer';
import axios from 'axios';
import { ARIELLE_ENDPOINTS } from '../config/apiEndpoints';
import useAuth from '../hooks/useAuth';
import { ITask } from '../data_models/task';
import ArielleSpeechBubble from '../components/aiAgent/SpeechBubble';
import { creatingProjectDraftPool, creatingProjectDraftWorkingPhrases, creatingRoadmapPool, creatingRoadmapWorkingPhrases } from './helper/arielleSentences';
import { CustomError } from '../data_models/error/customError';
import useDisplayNotifications from '../hooks/useDisplayNotifications';
import useRouting from '../hooks/useRouting';
import { ArielleProcessing } from '../data_models/dynamicData';


interface ArielleProviderProps {
    children: React.ReactNode;
}

// Create the context
export const ArielleContext = createContext<ArielleContextType | undefined>(undefined);


export const ArielleProvider: React.FC<ArielleProviderProps> = React.memo(({ children }) => {
    // Connection state
    const timeToWaitBetweenRequests = 1000;

    const expectedMilliToCompleteProjectDetails = 120000;
    const expectedMilliToCompleteProjectDetailsChange = 120000;
    const expectedMilliToCompleteProjectRoadmap = 120000;

    const projectDraftPollingInterval = 2000;
    const roadmapPollingInterval = 2000;
    const allowPollsWithoutResult = 1;
    const [pollsWithoutResult, setPollsWithoutResult] = useState(0);

    const [isConnected, setIsConnected] = useState(false);
    const [isProcessing, setIsProcessing] = useState<Set<EArielleProcessingType>>(new Set());
    const [hasError, setHasError] = useState<Map<EArielleProcessingType, CustomError>>(new Map());

    const [lastReceivedProjectDetails, setLastReceivedProjectDetails] = useState<ProjectDetailsResponse | null>(null);
    const [lastReceivedProjectRoadmap, setLastReceivedProjectRoadmap] = useState<ProjectRoadmapResponse | null>(null);
    const [lastReceivedBriefTaskDescription, setLastReceivedBriefTaskDescription] = useState<TaskBriefDescriptionResponse | null>(null);
    const [lastReceivedTaskTitle, setLastReceivedTaskTitle] = useState<TaskTitleResponse | null>(null);
    const [lastReceivedTaskDescription, setLastReceivedTaskDescription] = useState<TaskDescriptionResponse | null>(null);
    const [lastReceivedTaskDefinitionOfDone, setLastReceivedTaskDefinitionOfDone] = useState<TaskDefinitionOfDoneResponse | null>(null);

    const [projectDraftPollingActive, setProjectDraftPollingActive] = useState(false);
    const [roadmapPollingActive, setRoadmapPollingActive] = useState(false);

    const { socket, listenToArielle, sendToArielle } = useSocket();
    const { currentUser, currentUserType, locallyUpdateCurrentUser, locallyUpdateCurrentUserWallet } = useCurrentUser();
    const { addErrorNotification } = useDisplayNotifications();
    const { authToken } = useAuth();
    const { goToPricing } = useRouting();

    const [lastRequestedBriefTaskDescriptionTimestamp, setLastRequestedBriefTaskDescriptionTimestamp] = useState<number>(0);
    const [lastRequestedTaskTitleTimestamp, setLastRequestedTaskTitleTimestamp] = useState<number>(0);
    const [lastRequestedTaskDescriptionTimestamp, setLastRequestedTaskDescriptionTimestamp] = useState<number>(0);
    const [lastRequestedTaskDefinitionOfDoneTimestamp, setLastRequestedTaskDefinitionOfDoneTimestamp] = useState<number>(0);


    const [arielleCreatingRoadmapPoolIndex, setArielleCreatingRoadmapPoolIndex] = useState<number>(0);
    const [arielleCreatingProjectDetailsPoolIndex, setArielleCreatingProjectDetailsPoolIndex] = useState<number>(0);

    const [arielleCreatingProjectRoadmapKey, setArielleCreatingProjectRoadmapKey] = useState<number>(0);
    const [arielleCreatingProjectDetailsKey, setArielleCreatingProjectDetailsKey] = useState<number>(0);

    const renderArielleCreatingRoadmapNotification = () => {

        return (
            <ArielleSpeechBubble
                key={arielleCreatingProjectRoadmapKey}
                text={creatingRoadmapPool[arielleCreatingRoadmapPoolIndex]}
                timeInMs={expectedMilliToCompleteProjectRoadmap}
                completed={lastReceivedProjectRoadmap !== null}
                workingPhrases={creatingRoadmapWorkingPhrases}
            />
        );
    }

    const renderArielleCreatingProjectDetailsNotification = () => {
        return (
            <ArielleSpeechBubble
                key={arielleCreatingProjectDetailsKey}
                text={creatingProjectDraftPool[arielleCreatingProjectDetailsPoolIndex]}
                timeInMs={expectedMilliToCompleteProjectDetails}
                completed={lastReceivedProjectDetails !== null}
                workingPhrases={creatingProjectDraftWorkingPhrases}
            />
        );
    }

    const roadmapPolling = async (): Promise<void> => {
        const arielleProcessing = await getArielleProcessing();
        if (!arielleProcessing) {
            setPollsWithoutResult(pollsWithoutResult + 1);
        }
        if (arielleProcessing &&
            arielleProcessing.result &&
            arielleProcessing.processingType === EArielleProcessingType.CREATE_PROJECT_ROADMAP) {
            onReceiveProjectRoadmap(arielleProcessing.result as ProjectRoadmapResponse);
        }
    }

    const projectDraftPolling = async () => {
        const arielleProcessing = await getArielleProcessing();
        if (!arielleProcessing) {
            setPollsWithoutResult(pollsWithoutResult + 1);
        }
        if (arielleProcessing &&
            arielleProcessing.result &&
            arielleProcessing.processingType === EArielleProcessingType.CREATE_PROJECT_DETAILS) {
            onReceiveProjectDetails(arielleProcessing.result as ProjectDetailsResponse);
        }
    }

    useEffect(() => {
        if (pollsWithoutResult >= allowPollsWithoutResult) {
            setPollsWithoutResult(0);
            setRoadmapPollingActive(false);
            setProjectDraftPollingActive(false);
        }
    }, [pollsWithoutResult]);
    useEffect(() => {
        let roadmapInterval: NodeJS.Timeout;
        let projectDraftInterval: NodeJS.Timeout;

        if (roadmapPollingActive) {
            setPollsWithoutResult(0);
            roadmapInterval = setTimeout(() => {
                roadmapPolling();
                roadmapInterval = setInterval(() => {
                    roadmapPolling();
                }, roadmapPollingInterval);
            }, roadmapPollingInterval);
        }

        if (projectDraftPollingActive) {
            setPollsWithoutResult(0);
            projectDraftInterval = setTimeout(() => {
                projectDraftPolling();
                projectDraftInterval = setInterval(() => {
                    projectDraftPolling();
                }, projectDraftPollingInterval);
            }, projectDraftPollingInterval);
        }

        return () => {
            if (roadmapInterval) clearInterval(roadmapInterval);
            if (projectDraftInterval) clearInterval(projectDraftInterval);
        };
    }, [roadmapPollingActive, projectDraftPollingActive]);

    useEffect(() => {
        if (hasError.has(EArielleProcessingType.CREATE_PROJECT_ROADMAP)) {
            addErrorNotification(
                hasError.get(
                    EArielleProcessingType.CREATE_PROJECT_ROADMAP)?.errorCode?.message || 'Roadmap creation failed',
                "Roadmap creation failed",
                goToPricing,
                'Buy Credits'
            );
        }
    }, [hasError]);

    // Connection management
    useEffect(() => {
        try {
            if (currentUser && currentUserType) {
                listenToArielle(receiveArielleMessage);

                const handleArielleProcessing = async () => {

                    // initially check if arielle is processing something
                    const arielleProcessing = await getArielleProcessing();
                    if (arielleProcessing && !arielleProcessing.result) {
                        const newIsProcessingSet = new Set(isProcessing);
                        newIsProcessingSet.add(arielleProcessing.processingType as EArielleProcessingType);
                        setIsProcessing(newIsProcessingSet);

                        // poll for the result as redundance for socket connection
                        arielleProcessing.processingType === EArielleProcessingType.CREATE_PROJECT_DETAILS && setProjectDraftPollingActive(true);
                        arielleProcessing.processingType === EArielleProcessingType.CREATE_PROJECT_ROADMAP && setRoadmapPollingActive(true);
                    } else if (arielleProcessing && arielleProcessing.result) {
                        switch (arielleProcessing.processingType) {
                            case EArielleProcessingType.CREATE_PROJECT_DETAILS:
                                onReceiveProjectDetails(arielleProcessing.result as ProjectDetailsResponse);
                                break;
                            case EArielleProcessingType.CREATE_PROJECT_ROADMAP:
                                onReceiveProjectRoadmap(arielleProcessing.result as ProjectRoadmapResponse);
                                break;
                        }
                    }
                }
                handleArielleProcessing();

                // Cleanup function to remove the listener
                return () => {
                    socket?.off("arielle_response", receiveArielleMessage);
                };
            }
        } catch (error) {
            console.error("ArielleProvider", error)
        }
    }, [currentUser,
        currentUserType,
        listenToArielle,
        socket,
    ]);

    const timestampFilterSet = useRef(new Set<number>()); // id and last updated date

    const resetArielleProvider = async (): Promise<void> => {
        setLastReceivedProjectDetails(null);
        setLastReceivedProjectRoadmap(null);
        await checkIfCurrentlyProcessing();
    }

    const onReceiveProjectDetails = async (message: ProjectDetailsResponse) => {
        if (message.timestamp < (lastReceivedProjectDetails?.timestamp || 0)) {
            (process.env.NODE_ENV === 'development' || window.env.NODE_ENV === 'staging')
                && console.log('PROJECT_DETAILS_COMPLETE - (older than last received - discarded)', message);
            return;
        }
        (process.env.NODE_ENV === 'development' || window.env.NODE_ENV === 'staging')
            && console.log('PROJECT_DETAILS_COMPLETE', message);
        setLastReceivedProjectDetails(message);

        const newIsProcessing = new Set(isProcessing);
        const newHasError = new Map(hasError);
        newIsProcessing.delete(EArielleProcessingType.CREATE_PROJECT_DETAILS);
        newHasError.delete(EArielleProcessingType.CREATE_PROJECT_DETAILS);
        removeArielleProcessing();
        setIsProcessing(newIsProcessing);
        setHasError(newHasError);
        setProjectDraftPollingActive(false);
    }

    const onReceiveProjectRoadmap = (message: ProjectRoadmapResponse) => {
        if (message.timestamp < (lastReceivedProjectRoadmap?.timestamp || 0)) {
            (process.env.NODE_ENV === 'development' || window.env.NODE_ENV === 'staging')
                && console.log('PROJECT_ROADMAP_COMPLETE - (older than last received - discarded)', message);
            return;
        }
        (process.env.NODE_ENV === 'development' || window.env.NODE_ENV === 'staging')
            && console.log('PROJECT_ROADMAP_COMPLETE', message);
        setLastReceivedProjectRoadmap(message as ProjectRoadmapResponse);
        if (message.updatedEmployer && currentUserType === EUserType.EMPLOYER) {
            locallyUpdateCurrentUser(message.updatedEmployer);
        }
        if (message.updatedEmployerWallet && currentUserType === EUserType.EMPLOYER) {
            locallyUpdateCurrentUserWallet(message.updatedEmployerWallet);
        }
        const newIsProcessing = new Set(isProcessing);
        const newHasError = new Map(hasError);
        newIsProcessing.delete(EArielleProcessingType.CREATE_PROJECT_ROADMAP);
        newHasError.delete(EArielleProcessingType.CREATE_PROJECT_ROADMAP);
        removeArielleProcessing();
        setIsProcessing(newIsProcessing);
        setHasError(newHasError);
        setRoadmapPollingActive(false);
    }

    const onReceiveBriefTaskDescription = (message: TaskBriefDescriptionResponse) => {
        const newIsProcessing = new Set(isProcessing);
        const newHasError = new Map(hasError);
        setLastReceivedBriefTaskDescription(message as TaskBriefDescriptionResponse);
        newIsProcessing.delete(EArielleProcessingType.CREATE_BRIEF_TASK_DESCRIPTION);
        newHasError.delete(EArielleProcessingType.CREATE_BRIEF_TASK_DESCRIPTION);
        setProjectDraftPollingActive(false);
        setRoadmapPollingActive(false);
    }

    const onReceiveTaskTitle = (message: TaskTitleResponse) => {
        const newIsProcessing = new Set(isProcessing);
        const newHasError = new Map(hasError);
        setLastReceivedTaskTitle(message as TaskTitleResponse);
        newIsProcessing.delete(EArielleProcessingType.CREATE_TASK_TITLE);
        newHasError.delete(EArielleProcessingType.CREATE_TASK_TITLE);
        setProjectDraftPollingActive(false);
        setRoadmapPollingActive(false);
    }

    const onReceiveTaskDescription = (message: TaskDescriptionResponse) => {
        const newIsProcessing = new Set(isProcessing);
        const newHasError = new Map(hasError);
        setLastReceivedTaskDescription(message as TaskDescriptionResponse);
        newIsProcessing.delete(EArielleProcessingType.CREATE_TASK_DESCRIPTION);
        newHasError.delete(EArielleProcessingType.CREATE_TASK_DESCRIPTION);
        setProjectDraftPollingActive(false);
        setRoadmapPollingActive(false);
    }

    const onReceiveTaskDefinitionOfDone = (message: TaskDefinitionOfDoneResponse) => {
        const newIsProcessing = new Set(isProcessing);
        const newHasError = new Map(hasError);
        setLastReceivedTaskDefinitionOfDone(message as TaskDefinitionOfDoneResponse);
        newIsProcessing.delete(EArielleProcessingType.CREATE_TASK_DEFINITION_OF_DONE);
        newHasError.delete(EArielleProcessingType.CREATE_TASK_DEFINITION_OF_DONE);
        setProjectDraftPollingActive(false);
        setRoadmapPollingActive(false);
    }

    const receiveArielleMessage = (message: ArielleResponse) => {
        if (timestampFilterSet.current.has(message.timestamp)) {
            return;
        }
        timestampFilterSet.current.add(message.timestamp);
        switch (message.type) {
            case EArielleMessageType.PROJECT_DETAILS_COMPLETE:
                onReceiveProjectDetails(message as ProjectDetailsResponse);
                break;
            case EArielleMessageType.PROJECT_ROADMAP_COMPLETE:
                onReceiveProjectRoadmap(message as ProjectRoadmapResponse);
                break;
            case EArielleMessageType.BRIEF_TASK_DESCRIPTION_COMPLETE:
                onReceiveBriefTaskDescription(message as TaskBriefDescriptionResponse);
                break;
            case EArielleMessageType.TASK_TITLE_COMPLETE:
                onReceiveTaskTitle(message as TaskTitleResponse);
                break;
            case EArielleMessageType.TASK_DESCRIPTION_COMPLETE:
                onReceiveTaskDescription(message as TaskDescriptionResponse);
                break;
            case EArielleMessageType.TASK_DEFINITION_OF_DONE_COMPLETE:
                onReceiveTaskDefinitionOfDone(message as TaskDefinitionOfDoneResponse);
                break;
            default:
                console.log('default -no action- receiveArielleMessage', message);
                break;
        }
    };

    const checkIfCurrentlyProcessing = async (): Promise<boolean> => {
        try {
            const response = await axios.get(`${ARIELLE_ENDPOINTS.GET_IS_PROCESSING}`, {
                headers: { Authorization: `${authToken}` }
            });
            return response.data.currentlyProcessing;
        } catch (error: any) {
            console.error('checkIfCurrentlyProcessing error', error);
            return false;
        }
    }

    const getArielleProcessing = async (): Promise<ArielleProcessing | null> => {
        try {
            const response = await axios.get(`${ARIELLE_ENDPOINTS.GET_ARIELLE_PROCESSING}`, {
                headers: { Authorization: `${authToken}` }
            });
            return response.data.arielleProcessing;
        } catch (error: any) {
            console.error('getArielleProcessing error', error);
            return null;
        }
    }

    const removeArielleProcessing = async (): Promise<boolean> => {
        try {
            const response = await axios.delete(`${ARIELLE_ENDPOINTS.DELETE_ARIELLE_PROCESSING}`, {
                headers: { Authorization: `${authToken}` }
            });
            return response.data.removed;
        } catch (error: any) {
            console.error('removeArielleProcessing error', error);
            return false;
        }
    }

    const requestProjectDefinition = async (briefDescription: string, createProjectDraft: boolean): Promise<ProjectDetailsResponse | null> => {
        if (!currentUser || !currentUserType) {
            return null;
        }

        if (isProcessing.has(EArielleProcessingType.CREATE_PROJECT_DETAILS)) {
            return null;
        }

        let employer: IEmployer = {};
        if (currentUserType === EUserType.EMPLOYER) {
            employer = currentUser as IEmployer;
        }

        // fetch response from backend
        try {
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.add(EArielleProcessingType.CREATE_PROJECT_DETAILS);
            setIsProcessing(newIsProcessing);
            setProjectDraftPollingActive(true);

            setArielleCreatingProjectDetailsPoolIndex(Math.floor(Math.random() * creatingProjectDraftPool.length));
            setArielleCreatingProjectDetailsKey(Math.random());

            const response = await axios.post(`${ARIELLE_ENDPOINTS.POST_PROJECT_DETAILS}`,
                {
                    briefDescription,
                    employer,
                    createProjectDraft
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // processing
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.delete(EArielleProcessingType.CREATE_PROJECT_DETAILS);
            setIsProcessing(newIsProcessing);
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const requestProjectRoadmap = async (projectId: string): Promise<ProjectRoadmapResponse | null> => {
        if (!currentUser || !currentUserType) {
            return null;
        }

        if (isProcessing.has(EArielleProcessingType.CREATE_PROJECT_ROADMAP)) {
            return null;
        }

        setArielleCreatingRoadmapPoolIndex(Math.floor(Math.random() * creatingRoadmapPool.length));
        setArielleCreatingProjectRoadmapKey(Math.random());
        setLastReceivedProjectRoadmap(null);
        const newIsProcessing = new Set(isProcessing);
        newIsProcessing.add(EArielleProcessingType.CREATE_PROJECT_ROADMAP);
        setIsProcessing(newIsProcessing);
        const newHasError = new Map(hasError);
        newHasError.delete(EArielleProcessingType.CREATE_PROJECT_ROADMAP);
        setHasError(newHasError);

        // fetch response from backend
        try {
            setRoadmapPollingActive(true);
            const response = await axios.post(`${ARIELLE_ENDPOINTS.POST_PROJECT_ROADMAP}`,
                {
                    projectId,
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.delete(EArielleProcessingType.CREATE_PROJECT_ROADMAP);
            setIsProcessing(newIsProcessing);

            // if error is instance of CustomError
            const customError = error.response.data as CustomError;
            if (customError) {
                const newHasError = new Map(hasError);
                newHasError.set(EArielleProcessingType.CREATE_PROJECT_ROADMAP, customError);
                setHasError(newHasError);
                return null;
            } else {
                console.error('requestProjectRoadmap error', error);
            }

            return null;
        }
    }

    const requestBriefTaskDescription = async (taskId: string, task?: ITask): Promise<TaskBriefDescriptionResponse | null> => {
        if (!currentUser || !currentUserType) {
            return null;
        }

        if (isProcessing.has(EArielleProcessingType.CREATE_BRIEF_TASK_DESCRIPTION)) {
            return null;
        }

        if (Date.now() - lastRequestedBriefTaskDescriptionTimestamp < timeToWaitBetweenRequests) {
            return null;
        }

        try {
            setLastReceivedBriefTaskDescription(null);
            setLastRequestedBriefTaskDescriptionTimestamp(Date.now());

            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.add(EArielleProcessingType.CREATE_BRIEF_TASK_DESCRIPTION);
            setIsProcessing(newIsProcessing);

            const newHasError = new Map(hasError);
            newHasError.delete(EArielleProcessingType.CREATE_BRIEF_TASK_DESCRIPTION);
            setHasError(newHasError);

            const response = await axios.post(`${ARIELLE_ENDPOINTS.POST_BRIEF_TASK_DESCRIPTION}`,
                {
                    taskId,
                    task
                },
                {
                    headers: { Authorization: `${authToken}` }
                });
            return response.data;
        } catch (error: any) {
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.delete(EArielleProcessingType.CREATE_BRIEF_TASK_DESCRIPTION);
            setIsProcessing(newIsProcessing);
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const requestTaskTitle = async (taskId: string, task?: ITask): Promise<TaskTitleResponse | null> => {
        if (!currentUser || !currentUserType) {
            return null;
        }

        if (isProcessing.has(EArielleProcessingType.CREATE_TASK_TITLE)) {
            return null;
        }

        if (Date.now() - lastRequestedTaskTitleTimestamp < timeToWaitBetweenRequests) {
            return null;
        }

        try {
            setLastReceivedTaskTitle(null);
            setLastRequestedTaskTitleTimestamp(Date.now());

            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.add(EArielleProcessingType.CREATE_TASK_TITLE);
            setIsProcessing(newIsProcessing);

            const response = await axios.post(`${ARIELLE_ENDPOINTS.POST_TASK_TITLE}`,
                { taskId, task },
                { headers: { Authorization: `${authToken}` } });
            return response.data;
        } catch (error: any) {
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.delete(EArielleProcessingType.CREATE_TASK_TITLE);
            setIsProcessing(newIsProcessing);
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const requestTaskDescription = async (taskId: string, task?: ITask): Promise<TaskDescriptionResponse | null> => {
        if (!currentUser || !currentUserType) {
            return null;
        }

        if (isProcessing.has(EArielleProcessingType.CREATE_TASK_DESCRIPTION)) {
            return null;
        }

        if (Date.now() - lastRequestedTaskDescriptionTimestamp < timeToWaitBetweenRequests) {
            return null;
        }

        try {
            setLastReceivedTaskDescription(null);
            setLastRequestedTaskDescriptionTimestamp(Date.now());

            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.add(EArielleProcessingType.CREATE_TASK_DESCRIPTION);
            setIsProcessing(newIsProcessing);

            const response = await axios.post(`${ARIELLE_ENDPOINTS.POST_TASK_DESCRIPTION}`,
                { taskId, task },
                { headers: { Authorization: `${authToken}` } });
            return response.data;
        } catch (error: any) {
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.delete(EArielleProcessingType.CREATE_TASK_DESCRIPTION);
            setIsProcessing(newIsProcessing);
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const requestTaskDefinitionOfDone = async (taskId: string, task?: ITask): Promise<TaskDefinitionOfDoneResponse | null> => {

        if (!currentUser || !currentUserType) {
            return null;
        }

        if (isProcessing.has(EArielleProcessingType.CREATE_TASK_DEFINITION_OF_DONE)) {
            return null;
        }

        if (Date.now() - lastRequestedTaskDefinitionOfDoneTimestamp < timeToWaitBetweenRequests) {
            return null;
        }

        try {
            setLastReceivedTaskDefinitionOfDone(null);
            setLastRequestedTaskDefinitionOfDoneTimestamp(Date.now());

            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.add(EArielleProcessingType.CREATE_TASK_DEFINITION_OF_DONE);
            setIsProcessing(newIsProcessing);

            const newHasError = new Map(hasError);
            newHasError.delete(EArielleProcessingType.CREATE_TASK_DEFINITION_OF_DONE);
            setHasError(newHasError);

            const response = await axios.post(`${ARIELLE_ENDPOINTS.POST_TASK_DEFINITION_OF_DONE}`,
                { taskId, task },
                { headers: { Authorization: `${authToken}` } });
            return response.data;
        } catch (error: any) {
            const newIsProcessing = new Set(isProcessing);
            newIsProcessing.delete(EArielleProcessingType.CREATE_TASK_DEFINITION_OF_DONE);
            setIsProcessing(newIsProcessing);
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const contextValue: ArielleContextType = {
        expectedMilliToCompleteProjectDetails,
        expectedMilliToCompleteProjectDetailsChange,
        expectedMilliToCompleteProjectRoadmap,
        isConnected,
        isProcessing,
        hasError,
        lastReceivedProjectDetails,
        lastReceivedProjectRoadmap,
        lastReceivedBriefTaskDescription,
        lastReceivedTaskTitle,
        lastReceivedTaskDescription,
        lastReceivedTaskDefinitionOfDone,
        setLastReceivedProjectRoadmap,
        setLastReceivedBriefTaskDescription,
        setLastReceivedTaskTitle,
        setLastReceivedTaskDescription,
        setLastReceivedTaskDefinitionOfDone,
        setLastRequestedBriefTaskDescriptionTimestamp,
        setLastRequestedTaskTitleTimestamp,
        setLastRequestedTaskDescriptionTimestamp,
        setLastRequestedTaskDefinitionOfDoneTimestamp,
        resetArielleProvider,
        checkIfCurrentlyProcessing,
        getArielleProcessing,
        removeArielleProcessing,
        requestProjectDefinition,
        requestProjectRoadmap,
        requestBriefTaskDescription,
        requestTaskTitle,
        requestTaskDescription,
        requestTaskDefinitionOfDone,
        renderArielleCreatingRoadmapNotification,
        renderArielleCreatingProjectDetailsNotification
    };

    return (
        <ArielleContext.Provider value={contextValue}>
            {children}
        </ArielleContext.Provider>
    );
});
